/* eslint-disable max-len */
import merge from 'lodash/merge'
import get from 'lodash/get'
import set from 'date-fns/set'
import addDays from 'date-fns/addDays'
import { canTypeCompanyDisplayName } from '../../../state/selectors/user/user'
import { ApplyVia, ApplyViaEasyApply, ApplyViaExternal, ApplyViaJobBoard, Draft } from '../../../state/reducers/draft/draft'
import type { JobContent, UpgradedJobPostingProduct } from '../../../state/reducers/jobs/jobs'
import type { JobPostingProduct } from '../../../state/reducers/entitlement/entitlement'
import type { DashboardUser } from '../../../schema/user'

type Args = {
  jobContent: JobContent,
  product: JobPostingProduct | UpgradedJobPostingProduct,
  user: DashboardUser,
  userCanTypeAlternativeContactInfo: boolean,
  mergeContactInfo?: boolean
};

const determineCompanyOptions = (
  mainCompanyName: string,
  alternativeNames: Array<string>,
): Array<string> => {
  const options = [...alternativeNames]
  options.push(mainCompanyName)

  return options
}

const determineCompanyName = (
  mainCompanyName: string,
  alternativeNames: Array<string>,
  user: DashboardUser,
  current: string = '',
): string => {
  if (!user.isVerified) {
    return current || mainCompanyName || ''
  }

  if (current && canTypeCompanyDisplayName(user)) {
    return current
  }

  if (current && determineCompanyOptions(mainCompanyName, alternativeNames).includes(current)) {
    return current
  }

  if (alternativeNames.length >= 1) {
    return alternativeNames[0]
  }
  return mainCompanyName
}

const isInternalApply = (apply: ApplyVia): apply is ApplyViaJobBoard => apply.via === 'jobboard'
const isExternalApply = (apply: ApplyVia): apply is ApplyViaExternal => apply.via === 'external'
const isEasyApply = (apply: ApplyVia): apply is ApplyViaEasyApply => apply.via === 'easyapply'

export const mapApplyVia = (applyVia: ApplyVia, user: DashboardUser) => {
  if (isInternalApply(applyVia)) {
    return {
      ...applyVia,
      applyOption: 'ByEmail',
      firstName: applyVia.firstName || user.firstName,
      lastName: applyVia.lastName || user.lastName,
      emailAddress: applyVia.emailAddress || user.emailAddress,
    }
  }

  if (isExternalApply(applyVia)) {
    return {
      ...applyVia,
      applyOption: 'ByExternalUrl',
      url: applyVia.url || '',
    }
  }

  // istanbul ignore else
  if (isEasyApply(applyVia)) {
    return {
      ...applyVia,
      applyOption: 'ByEasyApply',
    }
  }
}

const mapAddress = (mergeContactInfo, contactInformation, user) => {
  return {
    streetNameAndHouseNumber: mergeContactInfo ? (contactInformation?.address?.streetNameAndHouseNumber || user?.company?.postalAddress?.streetNameAndHouseNumber) : contactInformation?.address?.streetNameAndHouseNumber || '',
    zipCode: mergeContactInfo ? (contactInformation?.address?.zipCode || user?.company?.postalAddress?.zipCode) : contactInformation?.address?.zipCode || '',
    city: mergeContactInfo ? (contactInformation?.address?.city || user?.company?.postalAddress?.city) : contactInformation?.address?.city || '',
  }
}

const mapContact = (mergeContactInfo, contactInformation, user) => {
  return {
    name: {
      firstName: mergeContactInfo ? (contactInformation?.contact?.name?.firstName || user?.firstName) : contactInformation?.contact?.name?.firstName || '',
      lastName: mergeContactInfo ? (contactInformation?.contact?.name?.lastName || user?.lastName) : contactInformation?.contact?.name?.lastName || '',
    },
    emailAddress: mergeContactInfo ? (contactInformation?.contact?.emailAddress || user?.emailAddress) : contactInformation?.contact?.emailAddress || '',
    phoneNumber: mergeContactInfo ? (contactInformation?.contact?.phoneNumber || user?.phoneNumber) : contactInformation?.contact?.phoneNumber || '',
  }
}

const mapContactInfo = (mergeContactInfo, contactInformation, user, userCanTypeAlternativeContactInfo) => {
  return userCanTypeAlternativeContactInfo ? {
    contact: mapContact(mergeContactInfo, contactInformation, user),
    address: mapAddress(mergeContactInfo, contactInformation, user),
  } : {
    contact: {
      name: {
        firstName: user?.firstName,
        lastName: user?.lastName,
      },
      emailAddress: user?.emailAddress,
      phoneNumber: user?.phoneNumber,
    },
    address: {
      streetNameAndHouseNumber: user?.company?.postalAddress?.streetNameAndHouseNumber,
      zipCode: user?.company?.postalAddress?.zipCode,
      city: user?.company?.postalAddress?.city,
    },
  }
}

const jobContentToDraft = ({
  jobContent,
  product,
  user,
  userCanTypeAlternativeContactInfo,
  mergeContactInfo,
}: Args): Draft => {
  // eslint-disable-next-line max-len
  const { publicationPeriod, location, applyVia, company, customFields, contactInformation } = jobContent

  const availableCompanyNames = determineCompanyOptions(
    user.company.name,
    user.company.alternativeNames
  )

  const selectedCompany = determineCompanyName(
    user.company.name,
    user.company.alternativeNames,
    user,
    company?.name,
  )

  const contactInfo = mapContactInfo(mergeContactInfo, contactInformation, user, userCanTypeAlternativeContactInfo)
  
  const website = mergeContactInfo ? (contactInformation?.website || user?.company?.website) : contactInformation?.website
  // istanbul ignore else
  if (website !== '') { (contactInfo as any).website = website }

  const draft = merge({}, jobContent, {
    /*
     * It was decided to always use local times when dealing with dates, assuming that the
     * client is always in the same timezone as the server.
     */
    publicationPeriod: {
      start: set(
        publicationPeriod.start
          ? new Date(publicationPeriod.start)
          : new Date(),
        {
          hours: 0,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
        }
      ),
      end: publicationPeriod.end
        ? set(new Date(publicationPeriod.end), {
          hours: 0,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
        })
        : addDays(
          set(new Date(), {
            hours: 0,
            minutes: 0,
            seconds: 0,
            milliseconds: 0,
          }),
          product.daysOnline - 1
        ),
    },
    location: {
      postalCode: location.postalCode || get(user, 'address.zipCode'),
    },
    applyVia: mapApplyVia(applyVia, user),
    customFields: customFields || [],
    company: {
      name: selectedCompany,
      availableNames: availableCompanyNames,
      companyType: user.company.companyType,
      description: company?.description ?? '',
    },
    contactInformation: contactInfo
  })
  const logoKey = jobContent.logo?.key || user.company.defaultLogo?.key
  // istanbul ignore else
  if (logoKey) { draft.logo = { key: logoKey } }
  if (draft.company.website === '') {
    delete draft.company.website
  }

  if (draft.contract && !draft.contract.remoteWork) {
    draft.contract.remoteWork = ''
  }

  return draft
}

export default jobContentToDraft
